import {
    type FC,
    useContext,
    useState,
    useEffect,
    type Dispatch,
    type SetStateAction,
} from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import { useRelayEnvironment } from 'react-relay';
import classNames from 'classnames';

import FormButton from './sharedComponents/FormButton.tsx';
import { ErrorMessage } from 'dibs-elements/exports/ErrorMessage.tsx';
import { SellerApplicationFormInventorySample } from './SellerApplicationFormInventorySample.tsx';

import SellerFormContext from './SellerFormContext.tsx';

import { updateSellerApplicationLeadMutation } from './mutations/updateSellerApplicationLead.ts';
import { trackSellerApplicationEvent } from '../helpers/sellerApplicationTracking.ts';

import { type SellerApplicationFormInventorySample_viewer$key } from './__generated__/SellerApplicationFormInventorySample_viewer.graphql.ts';

import dibsCss from 'dibs-css';

export const stepTitle = (
    <FormattedMessage
        defaultMessage="Inventory Sample"
        id="SellerApplicationForm.inventoryDetails.title"
    />
);

type Props = {
    viewer: SellerApplicationFormInventorySample_viewer$key;
    setFormError: Dispatch<SetStateAction<boolean>>;
};

const SellerApplicationFormInventoryDetails: FC<Props> = ({ viewer, setFormError }) => {
    const environment = useRelayEnvironment();
    const { dispatch, state } = useContext(SellerFormContext);
    const [isLoading, setIsLoading] = useState(false);
    const [notAllItemsError, setNotAllItemsError] = useState(false);

    const NUM_ITEMS_REQUIRED = 5;
    const { items } = state.formDataBusinessInventory;

    useEffect(() => {
        if (notAllItemsError && items.length === NUM_ITEMS_REQUIRED) {
            setNotAllItemsError(false);
        }
    }, [items.length, notAllItemsError]);

    const submit = async (): Promise<void> => {
        if (items.length === NUM_ITEMS_REQUIRED) {
            setNotAllItemsError(false);
            try {
                setIsLoading(true);
                await updateSellerApplicationLeadMutation(environment, {
                    recordId: state?.leadId || '',
                    step: 'inventoryDetails',
                    businessItems: items.map(item => ({
                        ...item,
                        creatorArtistDesigner: item.creatorArtistDesigner?.label,
                    })),
                });
                trackSellerApplicationEvent({
                    type: 'professional',
                    action: 'step 3 submit',
                    interactionName: 'submit_page_3',
                    stepInteractionType: 'click on Continue',
                });
                dispatch({ type: 'SET_STEP', step: 'BUSINESS_STEP5' });
            } catch (err) {
                setIsLoading(false);
                setFormError(true);
            }
            setIsLoading(false);
        } else {
            setNotAllItemsError(true);
        }
    };

    return (
        <div className={dibsCss.mbLarge} data-tn="application-form-inventory-details">
            <div className={classNames(dibsCss.sassyFontBody, dibsCss.mbSmall)}>
                <FormattedMessage
                    defaultMessage="Submit five items that best represent the inventory you'd like to sell on 1stDibs including details like price, origin, materials and dimensions."
                    id="SellerApplicationForm.inventoryDetails.instructions"
                />
            </div>
            <div className={dibsCss.wFull}>
                {[...Array(NUM_ITEMS_REQUIRED)].map((_, i) => (
                    <SellerApplicationFormInventorySample
                        key={i}
                        index={i}
                        viewer={viewer}
                        disabled={i === 0 ? false : !Object.keys(items[i - 1] || {}).length}
                    />
                ))}
                {notAllItemsError ? (
                    <ErrorMessage
                        dataTn="tracking-number-error"
                        message={
                            <FormattedMessage
                                defaultMessage="Please add {number} more item(s)."
                                id="SellerApplicationForm.inventoryDetails.notAllItems"
                                values={{ number: NUM_ITEMS_REQUIRED - items.length }}
                            />
                        }
                    />
                ) : null}
                <FormButton isLoading={isLoading} onSubmit={submit} />
            </div>
        </div>
    );
};

export default SellerApplicationFormInventoryDetails;
